import { Button } from '@/components/ui/button';

import { useEnv } from '@/components/providers/env-provider';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ExternalLink, Menu, PlusIcon } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { NavLink } from 'react-router-dom';
import { useState } from 'react';
import { useUser } from '@/components/providers/user-provider';
import { ModeToggle } from '@/components/mode-toggle';
import { hasPermission } from '@/utils/validation';
import { RESTORE_PERMISSIONS } from '@/config/feature-permissions';
import { useAuth0 } from '@auth0/auth0-react';
import { emailToColor } from '@/utils/emailToColor';
import { appEnv } from '@/config/env';

function Header() {
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);

  const { role } = useUser();
  const { env } = useEnv();

  return (
    <header className='mt-5 flex flex-wrap lg:flex-nowrap gap-5 justify-between mb-10 lg:mb-20'>
      <NavLink
        to='/'
        className='flex gap-x-4 scroll-m-20 font-semibold tracking-tight lg:text-3xl items-center'
      >
        <img
          src='https://www.ikea.com/global/assets/logos/brand/ikea.svg'
          alt='ikea logo'
        />
        Planner Entrypoints
      </NavLink>

      <div className='flex gap-6 items-center lg:flex-wrap justify-center'>
        <NavLink
          to={appEnv.docsUrl}
          target='__blank'
          className='flex gap-2 hover:text-gray-500 items-center'
        >
          <ExternalLink />
          Documentation
        </NavLink>
        <SwitchEnv />
        <NavLink
          to='/request-user-access'
          className='flex items-center text-sm font-medium gap-2 h-8 border-solid border-2 rounded p-4 !border-slate-800 !bg-slate-800 !text-white dark:bg-white dark:border-white dark:text-black cursor-default'
        >
          <PlusIcon size={16} />
          Request access
        </NavLink>
        <Profile />
        <ModeToggle />
        <Sheet
          open={isSidebarOpen}
          onOpenChange={(open) => setIsSidebarOpen(open)}
        >
          <SheetTrigger className='flex items-center'>
            <Menu />
          </SheetTrigger>
          <SheetContent className='flex flex-col justify-between'>
            <div className='flex flex-col mt-16 text-center text-3xl space-y-6'>
              <NavLink
                to='/'
                onClick={() => {
                  setIsSidebarOpen(false);
                }}
              >
                Planners
              </NavLink>
              {role === 'admin' && (
                <>
                  <NavLink
                    to='/users'
                    onClick={() => {
                      setIsSidebarOpen(false);
                    }}
                  >
                    Users
                  </NavLink>
                  <NavLink
                    to='/review-user-access'
                    onClick={() => {
                      setIsSidebarOpen(false);
                    }}
                  >
                    Review user access
                  </NavLink>
                </>
              )}
              <NavLink
                to='/jobs'
                onClick={() => {
                  setIsSidebarOpen(false);
                }}
              >
                Translation jobs
              </NavLink>
              {hasPermission(RESTORE_PERMISSIONS[env], role) && (
                <NavLink
                  to='/backup'
                  onClick={() => {
                    setIsSidebarOpen(false);
                  }}
                >
                  Backup
                </NavLink>
              )}
              <NavLink
                to='/fragment-preview'
                onClick={() => {
                  setIsSidebarOpen(false);
                }}
              >
                Fragment preview
              </NavLink>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}

function SwitchEnv() {
  const { env, setEnv } = useEnv();
  return (
    <>
      <Tabs defaultValue={env} className='w-[400px]'>
        <TabsList className='grid w-full grid-cols-2'>
          <TabsTrigger value='cte' onClick={() => setEnv('cte')}>
            CTE
          </TabsTrigger>
          <TabsTrigger value='production' onClick={() => setEnv('production')}>
            Production
          </TabsTrigger>
        </TabsList>
      </Tabs>
    </>
  );
}

function Profile() {
  const { user, logout, loginWithPopup, isAuthenticated } = useAuth0();
  return (
    <div className='flex items-center'>
      {!isAuthenticated && (
        <Button
          size='lg'
          onClick={() => loginWithPopup()}
          className='flex mr-2 items-center text-sm font-medium gap-2 h-8 border-solid border-2 rounded p-4 !border-slate-800 !bg-slate-800 !text-white dark:bg-white dark:border-white dark:text-black cursor-default'
        >
          Login
        </Button>
      )}
      {isAuthenticated && (
        <Button
          size='lg'
          onClick={() =>
            logout({ logoutParams: { returnTo: window.location.origin } })
          }
          className='flex mr-2 items-center text-sm font-medium gap-2 h-8 border-solid border-2 rounded p-4 !border-slate-800 !bg-slate-800 !text-white dark:bg-white dark:border-white dark:text-black cursor-default'
        >
          Sign out
        </Button>
      )}
      {user?.email && (
        <div
          className='flex items-center justify-center w-8 h-8 rounded-full text-white text-sm font-medium'
          style={{ backgroundColor: `${emailToColor(user.email)}` }}
        >
          {user.email.slice(0, 2).toLocaleUpperCase()}
        </div>
      )}
    </div>
  );
}

export default Header;
